"use client";

import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/nextjs";
import { identifyUser, resetUser } from "@/lib/posthog-helpers";
import { useAuthStore } from "@/stores/auth-store";

/**
 * Keeps PostHog and Sentry pointed at whoever is signed in.
 *
 * Mounted only where there can BE somebody signed in (components/app-runtime.tsx). It reads
 * the auth store, and that store reaches the Supabase browser client, so mounting it on the
 * website would put 230KB of authentication library on a page with no account behind it.
 */
export function PostHogIdentity() {
  // The last userId handed to PostHog and Sentry, so nothing fires twice.
  // null = anonymous, string = the userId currently identified.
  const prevUserIdRef = useRef<string | null>(null);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (user?.id) {
      if (user.id === prevUserIdRef.current) return;
      prevUserIdRef.current = user.id;

      // Apple ATT: do not send email as an identifying trait from the iOS app. Reviewers
      // flag third-party analytics tying cross-app identifiers to personal data with no ATT
      // prompt. Web and Android are unchanged.
      const capacitor = (window as unknown as {
        Capacitor?: { getPlatform?: () => string; isNativePlatform?: () => boolean };
      }).Capacitor;
      const isIosNative =
        capacitor?.isNativePlatform?.() === true && capacitor?.getPlatform?.() === "ios";

      identifyUser(user.id, isIosNative ? undefined : { email: user.email });
      Sentry.setUser({ id: user.id, email: user.email ?? undefined });
    } else {
      if (prevUserIdRef.current === null) return;
      prevUserIdRef.current = null;
      resetUser();
      Sentry.setUser(null);
    }
  }, [user?.id, user?.email]);

  return null;
}
